/**
 * 🟥 CUADRO NEGRO V7 — WorkflowsPanel.
 *
 * Modal con lista de workflows (.github/workflows/*.yml) y runs por workflow.
 * Permite:
 *   - Ver runs filtrables por status.
 *   - Ver logs (con summary IA) de un run.
 *   - Ejecutar workflow manualmente (workflow_dispatch).
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Play, Workflow, X, Loader2, ExternalLink, CheckCircle2, XCircle, Circle, FileText, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '../config/api';

const API = `${BACKEND_URL}/api`;

const conclusionStyles = {
  success: { color: 'text-emerald-400', Icon: CheckCircle2 },
  failure: { color: 'text-rose-400', Icon: XCircle },
  cancelled: { color: 'text-slate-400', Icon: Circle },
  null: { color: 'text-amber-400', Icon: Loader2 },
};

export default function WorkflowsPanel({
  moduleId, githubSource, isEs = true, onClose, currentBranch = 'main',
}) {
  const [workflows, setWorkflows] = useState([]);
  const [selectedWfId, setSelectedWfId] = useState(null);
  const [runs, setRuns] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [loadingWfs, setLoadingWfs] = useState(true);
  const [loadingRuns, setLoadingRuns] = useState(false);

  const [dispatchRef, setDispatchRef] = useState(currentBranch);
  const [dispatching, setDispatching] = useState(false);

  const [logRun, setLogRun] = useState(null);
  const [logData, setLogData] = useState(null);
  const [loadingLog, setLoadingLog] = useState(false);

  const repoLabel = githubSource ? `${githubSource.owner}/${githubSource.repo}` : '';

  const fetchWorkflows = async () => {
    setLoadingWfs(true);
    try {
      const res = await axios.get(`${API}/github/workflows`, { params: { module_id: moduleId } });
      const list = res.data?.workflows || [];
      setWorkflows(list);
      if (list.length > 0 && !selectedWfId) setSelectedWfId(list[0].id);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message;
      toast.error(isEs ? `Workflows falló: ${detail}` : `Workflows failed: ${detail}`);
    } finally {
      setLoadingWfs(false);
    }
  };

  const fetchRuns = async () => {
    if (!selectedWfId) return;
    setLoadingRuns(true);
    try {
      const res = await axios.get(`${API}/github/workflow-runs`, {
        params: { module_id: moduleId, workflow_id: selectedWfId, status: statusFilter },
      });
      setRuns(res.data?.runs || []);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message;
      toast.error(isEs ? `Runs falló: ${detail}` : `Runs failed: ${detail}`);
    } finally {
      setLoadingRuns(false);
    }
  };

  useEffect(() => {
    const id = setTimeout(() => { fetchWorkflows(); }, 0);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleId]);

  useEffect(() => {
    const id = setTimeout(() => { fetchRuns(); }, 0);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWfId, statusFilter]);

  const handleDispatch = async () => {
    if (!selectedWfId) return;
    setDispatching(true);
    try {
      await axios.post(`${API}/github/workflow-dispatch`, {
        module_id: moduleId,
        workflow_id: selectedWfId,
        ref: (dispatchRef || currentBranch).trim(),
      });
      toast.success(isEs ? 'Workflow ejecutado · refresca en unos segundos' : 'Workflow dispatched');
      setTimeout(fetchRuns, 1500);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message;
      toast.error(isEs ? `Dispatch falló: ${detail}` : `Dispatch failed: ${detail}`);
    } finally {
      setDispatching(false);
    }
  };

  const handleViewLog = async (run) => {
    setLogRun(run);
    setLogData(null);
    setLoadingLog(true);
    try {
      const res = await axios.get(`${API}/github/workflow-run-log`, {
        params: { module_id: moduleId, run_id: run.id },
      });
      setLogData(res.data);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message;
      toast.error(isEs ? `No se pudo cargar log: ${detail}` : `Log failed: ${detail}`);
      setLogRun(null);
    } finally {
      setLoadingLog(false);
    }
  };

  const selectedWf = workflows.find((w) => w.id === selectedWfId);

  return (
    <div
      className="fixed inset-0 z-[8500] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="workflows-panel"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="w-full max-w-5xl h-[88vh] bg-gradient-to-b from-slate-900 to-slate-950 border border-emerald-500/30 rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        <header className="flex items-center justify-between px-5 py-3 border-b border-slate-800 bg-gradient-to-r from-emerald-900/40 to-teal-900/30">
          <div className="flex items-center gap-2">
            <Workflow className="w-5 h-5 text-emerald-300" />
            <div>
              <h2 className="text-base font-bold text-white">{isEs ? 'GitHub Actions' : 'GitHub Actions'}</h2>
              <p className="text-[11px] text-emerald-300/80">{repoLabel}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="workflows-close-btn"
            className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        <div className="flex-1 min-h-0 flex">
          {/* Workflows sidebar */}
          <aside className="w-60 border-r border-slate-800 overflow-y-auto py-2 shrink-0">
            <div className="px-3 pb-2 text-[10px] uppercase tracking-wider text-slate-500">
              {isEs ? 'Workflows' : 'Workflows'}
            </div>
            {loadingWfs ? (
              <div className="flex justify-center py-6 text-slate-400">
                <Loader2 className="w-4 h-4 animate-spin" />
              </div>
            ) : workflows.length === 0 ? (
              <div className="px-3 py-4 text-[11px] text-slate-500 text-center">
                {isEs
                  ? 'No se detectaron workflows (.github/workflows).'
                  : 'No workflows detected.'}
              </div>
            ) : (
              workflows.map((wf) => (
                <button
                  key={wf.id}
                  type="button"
                  onClick={() => setSelectedWfId(wf.id)}
                  data-testid={`workflow-item-${wf.id}`}
                  className={`w-full text-left px-3 py-2 text-xs border-l-2 transition-colors ${
                    wf.id === selectedWfId
                      ? 'bg-emerald-500/10 border-emerald-400 text-white'
                      : 'border-transparent text-slate-400 hover:bg-slate-800/50 hover:text-white'
                  }`}
                >
                  <div className="font-medium truncate">{wf.name}</div>
                  <div className="text-[10px] text-slate-500 truncate font-mono">{wf.path}</div>
                </button>
              ))
            )}
          </aside>

          {/* Runs area */}
          <section className="flex-1 min-w-0 flex flex-col">
            {selectedWf && (
              <div className="px-4 py-2 border-b border-slate-800 flex items-center gap-2 flex-wrap">
                <div className="text-xs text-slate-300 flex-1 min-w-0">
                  <span className="font-semibold">{selectedWf.name}</span>
                  <span className="text-slate-500 ml-2 font-mono">{selectedWf.path}</span>
                </div>
                <div className="flex items-center gap-0 bg-slate-800 rounded-md p-0.5">
                  {['all', 'success', 'failure', 'cancelled'].map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setStatusFilter(s)}
                      data-testid={`runs-filter-${s}`}
                      className={`px-2 py-1 text-[10px] rounded ${
                        statusFilter === s ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:text-white'
                      }`}
                    >
                      {s === 'all' ? (isEs ? 'Todos' : 'All')
                        : s === 'success' ? 'OK'
                        : s === 'failure' ? 'Fail'
                        : (isEs ? 'Cancel' : 'Cancel')}
                    </button>
                  ))}
                </div>
                <input
                  type="text"
                  value={dispatchRef}
                  onChange={(e) => setDispatchRef(e.target.value)}
                  className="px-2 py-1 text-[11px] bg-slate-950 border border-slate-700 rounded text-slate-200 font-mono w-32"
                  placeholder="ref"
                  disabled={dispatching}
                  data-testid="workflow-dispatch-ref"
                />
                <button
                  type="button"
                  onClick={handleDispatch}
                  disabled={dispatching}
                  data-testid="workflow-dispatch-btn"
                  className="flex items-center gap-1 px-2.5 py-1 text-xs rounded-md bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-60"
                  title={isEs ? 'Ejecutar workflow' : 'Run workflow'}
                >
                  {dispatching ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                  {isEs ? 'Ejecutar' : 'Run'}
                </button>
              </div>
            )}
            <div className="flex-1 overflow-y-auto px-3 py-2 space-y-1">
              {loadingRuns ? (
                <div className="flex justify-center py-8 text-slate-400">
                  <Loader2 className="w-5 h-5 animate-spin" />
                </div>
              ) : runs.length === 0 ? (
                <div className="text-center text-slate-500 text-xs py-8">
                  {isEs ? 'Sin runs en este filtro.' : 'No runs.'}
                </div>
              ) : (
                runs.map((run) => {
                  const meta = conclusionStyles[run.conclusion ?? 'null'] || conclusionStyles.null;
                  const Icon = meta.Icon;
                  return (
                    <div
                      key={run.id}
                      className="flex items-center gap-2 p-2 rounded-md bg-slate-900 border border-slate-800 hover:border-emerald-500/30"
                      data-testid={`run-row-${run.id}`}
                    >
                      <Icon className={`w-4 h-4 shrink-0 ${meta.color} ${run.status === 'in_progress' ? 'animate-spin' : ''}`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-white truncate">{run.commit_message || run.name}</div>
                        <div className="text-[10px] text-slate-500 flex items-center gap-2 flex-wrap font-mono">
                          <span>{run.branch}</span>
                          <span>·</span>
                          <span>{run.commit_sha?.slice(0, 7)}</span>
                          {run.actor && <><span>·</span><span>{run.actor}</span></>}
                          {run.updated_at && <><span>·</span><span>{new Date(run.updated_at).toLocaleString()}</span></>}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleViewLog(run)}
                        data-testid={`run-log-${run.id}`}
                        title={isEs ? 'Ver log + summary IA' : 'View log + AI summary'}
                        className="p-1.5 rounded-md bg-indigo-600/80 hover:bg-indigo-500 text-white"
                      >
                        <FileText className="w-3 h-3" />
                      </button>
                      {run.html_url && (
                        <a
                          href={run.html_url}
                          target="_blank"
                          rel="noreferrer"
                          className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-slate-800"
                          title={isEs ? 'Abrir en GitHub' : 'Open in GitHub'}
                        >
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </section>
        </div>

        {/* Log viewer */}
        {logRun && (
          <div
            className="fixed inset-0 z-[8600] bg-black/85 flex items-center justify-center p-4"
            onClick={(e) => { if (e.target === e.currentTarget) { setLogRun(null); setLogData(null); } }}
          >
            <div className="w-full max-w-4xl max-h-[90vh] bg-slate-950 border border-indigo-500/30 rounded-2xl flex flex-col">
              <header className="flex items-center justify-between px-4 py-2 border-b border-slate-800">
                <div>
                  <div className="text-sm text-white font-medium truncate">{logRun.commit_message || logRun.name}</div>
                  <div className="text-[10px] text-slate-500 font-mono">{logRun.commit_sha?.slice(0, 7)} · {logRun.branch}</div>
                </div>
                <button
                  type="button"
                  onClick={() => { setLogRun(null); setLogData(null); }}
                  className="p-1.5 rounded text-slate-400 hover:text-white"
                  data-testid="log-modal-close"
                >
                  <X className="w-4 h-4" />
                </button>
              </header>
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {loadingLog ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-indigo-300" />
                  </div>
                ) : logData ? (
                  <>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-indigo-400 mb-1 flex items-center gap-1">
                        <Sparkles className="w-3 h-3" /> {isEs ? 'Resumen IA' : 'AI Summary'}
                      </div>
                      <pre className="text-xs whitespace-pre-wrap text-slate-200 bg-slate-900 p-3 rounded border border-slate-800">
                        {logData.summary || (isEs ? '(sin resumen)' : '(no summary)')}
                      </pre>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
                        {isEs ? `Log ${logData.truncated ? '(últimas 8KB)' : '(completo)'}` : `Log ${logData.truncated ? '(last 8KB)' : '(full)'}`}
                      </div>
                      <pre className="text-[10px] whitespace-pre font-mono text-emerald-200 bg-black p-3 rounded border border-slate-800 overflow-x-auto max-h-96 overflow-y-auto">
                        {logData.raw_tail || '(empty)'}
                      </pre>
                    </div>
                  </>
                ) : null}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
